<template>
  <section class="programs-section">
    <div class="programs-wrapper">

      <!-- ── Section Header ──────────────────────────────────────── -->
      <Motion
        :initial="{ opacity: 0, y: 30 }"
        :whileInView="{ opacity: 1, y: 0 }"
        :inViewOptions="{ once: true, margin: '-80px' }"
        :transition="{ duration: 0.7, ease: 'easeOut' }"
      >
        <div class="section-header">
          <h2 class="section-title">
            Programs Offered Through Our
            <span class="gradient-text">University Partners</span>
          </h2>
          <p class="section-subtitle">
            Explore undergraduate and postgraduate programs delivered through leading university
            partnerships and enhanced by Eduversity's industry-focused learning ecosystem.
          </p>
        </div>
      </Motion>

      <!-- ── Degree Filter ───────────────────────────────────────── -->
      <div class="filter-wrap">
        <div class="filter-panel">

          <!-- Under-Graduate group -->
          <div class="deg-group">
            <span class="group-label">
              <span v-html="iconGrad" />
              Under-Graduate
            </span>
            <div class="deg-chips">
              <button v-for="d in ugDegrees" :key="d" type="button"
                :class="['deg-chip', { active: activeDegree === d }]" @click="activeDegree = d">
                {{ d }}
                <span class="chip-count">{{ degreeMap[d].length }}</span>
              </button>
            </div>
          </div>

          <!-- Divider -->
          <div class="filter-divider" />

          <!-- Post-Graduate group -->
          <div class="deg-group">
            <span class="group-label">
              <span v-html="iconAward" />
              Post-Graduate
            </span>
            <div class="deg-chips">
              <button v-for="d in pgDegrees" :key="d" type="button"
                :class="['deg-chip', { active: activeDegree === d }]" @click="activeDegree = d">
                {{ d }}
                <span class="chip-count">{{ degreeMap[d].length }}</span>
              </button>
            </div>
          </div>

        </div>
      </div>

      <!-- ── Cards ───────────────────────────────────────────────── -->
      <Transition name="fade-slide" mode="out-in">
        <div :key="activeDegree" class="programs-grid">
          <Motion
            v-for="(prog, i) in degreeMap[activeDegree]"
            :key="prog.title"
            :initial="{ opacity: 0, y: 40 }"
            :whileInView="{ opacity: 1, y: 0 }"
            :inViewOptions="{ once: true, margin: '-60px' }"
            :transition="{ duration: 0.6, delay: i * 0.1, ease: 'easeOut' }"
          >
            <article class="prog-card" :style="{ '--accent': prog.accent, '--accent-rgb': prog.accentRgb }">
              <!-- Accent top bar -->
              <div class="prog-top-bar" />

              <!-- Media area (148px tall) — shows the specialization image -->
              <div class="prog-media">
                <img class="media-img" :src="prog.image" :alt="prog.title" loading="lazy" />
                <!-- Subtle bottom gradient for visual blending into the card -->
                <div class="media-overlay" />
                <!-- Small icon badge pinned top-left for branding -->
                <div class="media-icon-badge">
                  <span v-html="prog.icon" />
                </div>
              </div>

              <!-- Body -->
              <div class="prog-body">
                <span class="prog-number">{{ String(i + 1).padStart(2, '0') }}</span>

                <div class="prog-titles">
                  <h3 class="prog-title">{{ prog.title }}</h3>
                  <p v-if="prog.tag" class="prog-tag">{{ prog.tag }}</p>
                </div>

                <p class="prog-desc">{{ prog.desc }}</p>

                <!-- University partners -->
                <div class="prog-unis">
                  <span class="unis-label">
                    <span v-html="iconBuilding" />
                    Available At
                  </span>
                  <div class="uni-chips">
                    <span v-for="u in prog.universities" :key="u" class="uni-chip">
                      {{ u }}
                    </span>
                  </div>
                </div>

                <button type="button" class="prog-cta">
                  Enquire Now
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                    stroke-linecap="round" stroke-linejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>
            </article>
          </Motion>
        </div>
      </Transition>

    </div>
  </section>
</template>

<script setup lang="ts">
// ── Icons ─────────────────────────────────────────────────────────────────────
const iconGrad = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>`
const iconAward = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>`
const iconBuilding = `<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01"/></svg>`

// Card icons (28px)
const iconCode = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>`
const iconCpu = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="6" height="6"/><path d="M17 9h1a2 2 0 0 1 2 2v1M17 15h1a2 2 0 0 0 2-2v-1M7 9H6a2 2 0 0 0-2 2v1M7 15H6a2 2 0 0 1-2-2v-1M9 7V6a2 2 0 0 1 2-2h1M15 7V6a2 2 0 0 0-2-2h-1M9 17v1a2 2 0 0 0 2 2h1M15 17v1a2 2 0 0 1-2 2h-1"/></svg>`
const iconTerminal = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>`
const iconLayers = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>`
const iconNetwork = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="2.5"/><circle cx="5" cy="19" r="2.5"/><circle cx="19" cy="19" r="2.5"/><line x1="12" y1="7.5" x2="5" y2="16.5"/><line x1="12" y1="7.5" x2="19" y2="16.5"/><line x1="7.5" y1="19" x2="16.5" y2="19"/></svg>`
const iconTrending = `<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>`

// ── Types ─────────────────────────────────────────────────────────────────────
interface SpecCard {
  title: string
  tag?: string
  desc: string
  universities: string[]
  accent: string
  accentRgb: string
  icon: string
  image: string
}

// ── Image map (public/spec-images) ────────────────────────────────────────────
const IMG = {
  fullStack: '/spec-images/full-stack-development.jpg',
  rpa: '/spec-images/Robotic-process-automation.png',
  softwareDev: '/spec-images/software-development.png',
  csDesign: '/spec-images/computerscience-Design-system.jpg',
  aiDataSci: '/spec-images/ai-data-science.jpg',
  fintech: '/spec-images/fintech.jpeg',
} as const

type DegreeName =
  | 'B.Tech' | 'BCA'
  | 'M.Tech' | 'M.Tech Online'
  | 'MCA' | 'MCA Online'
  | 'MBA' | 'MBA Online'

// ── Degree filter groups ──────────────────────────────────────────────────────
const ugDegrees: DegreeName[] = ['B.Tech', 'BCA']
const pgDegrees: DegreeName[] = ['M.Tech', 'M.Tech Online', 'MCA', 'MCA Online', 'MBA', 'MBA Online']

// ── Degree → specializations map ─────────────────────────────────────────────
const degreeMap: Record<DegreeName, SpecCard[]> = {

  'B.Tech': [
    {
      title: 'Full Stack Development & Testing',
      tag: 'with AI & ML',
      desc: 'Build end-to-end web applications with AI-powered testing and modern full stack architecture across frontend, backend, and cloud.',
      universities: ['LTSU University'],
      accent: '#3b82f6', accentRgb: '59,130,246', icon: iconCode, image: IMG.fullStack,
    },
    {
      title: 'Robotic Process Automation',
      tag: 'with AI & ML',
      desc: 'Design and deploy intelligent bots that transform enterprise workflows using industry-leading RPA platforms and cognitive automation.',
      universities: ['LTSU University', 'Sandip University', 'Sanjay Ghodawat University'],
      accent: '#8b5cf6', accentRgb: '139,92,246', icon: iconCpu, image: IMG.rpa,
    },
    {
      title: 'Software Development',
      tag: 'with AI & ML',
      desc: 'Master modern software engineering enhanced with AI. Build production-grade systems using industry-proven methods and real-world projects.',
      universities: ['Sandip University', 'Sanjay Ghodawat University', 'Joy University'],
      accent: '#10b981', accentRgb: '16,185,129', icon: iconTerminal, image: IMG.softwareDev,
    },
    {
      title: 'Computer Science & Design System',
      desc: 'Combine deep CS fundamentals with design systems thinking to create impactful digital products and cohesive user experiences at scale.',
      universities: ['Canara Engineering College'],
      accent: '#ec4899', accentRgb: '236,72,153', icon: iconLayers, image: IMG.csDesign,
    },
    {
      title: 'Artificial Intelligence & Data Science',
      desc: 'Transform raw data into intelligent insights using machine learning models, data engineering pipelines, and production AI systems.',
      universities: ['Canara Engineering College'],
      accent: '#ff7800', accentRgb: '255,120,0', icon: iconNetwork, image: IMG.aiDataSci,
    },
  ],

  'BCA': [
    {
      title: 'Full Stack Development & Testing',
      tag: 'with AI & ML',
      desc: 'Build end-to-end web applications with AI-powered testing and modern full stack architecture across frontend, backend, and cloud.',
      universities: ['LTSU University'],
      accent: '#3b82f6', accentRgb: '59,130,246', icon: iconCode, image: IMG.fullStack,
    },
    {
      title: 'Robotic Process Automation',
      tag: 'with AI & ML',
      desc: 'Design and deploy intelligent bots that transform enterprise workflows using industry-leading RPA platforms and cognitive automation.',
      universities: ['LTSU University'],
      accent: '#8b5cf6', accentRgb: '139,92,246', icon: iconCpu, image: IMG.rpa,
    },
  ],

  'M.Tech': [
    {
      title: 'Full Stack Development & Testing',
      tag: 'with AI & ML',
      desc: 'Advanced full stack engineering with AI-powered testing, microservices architecture, and cloud-native deployment at enterprise scale.',
      universities: ['LTSU University'],
      accent: '#3b82f6', accentRgb: '59,130,246', icon: iconCode, image: IMG.fullStack,
    },
    {
      title: 'Robotic Process Automation',
      tag: 'with AI & ML',
      desc: 'Advanced RPA architecture, cognitive automation, and enterprise-grade intelligent process orchestration with AI-driven decision systems.',
      universities: ['LTSU University', 'Sandip University'],
      accent: '#8b5cf6', accentRgb: '139,92,246', icon: iconCpu, image: IMG.rpa,
    },
    {
      title: 'Software Development',
      tag: 'with AI & ML',
      desc: 'Research-driven software engineering, advanced system design, and AI integration for building enterprise-grade scalable solutions.',
      universities: ['Sandip University'],
      accent: '#10b981', accentRgb: '16,185,129', icon: iconTerminal, image: IMG.softwareDev,
    },
  ],

  'M.Tech Online': [
    {
      title: 'Full Stack Development & Testing',
      tag: 'with AI & ML',
      desc: 'Advanced full stack engineering with AI-powered testing, microservices architecture, and cloud-native deployment — fully online.',
      universities: ['Jain University'],
      accent: '#3b82f6', accentRgb: '59,130,246', icon: iconCode, image: IMG.fullStack,
    },
  ],

  'MCA': [
    {
      title: 'Full Stack Development & Testing',
      tag: 'with AI & ML',
      desc: 'Advanced full stack engineering with AI-powered testing, microservices architecture, and cloud-native deployment at enterprise scale.',
      universities: ['LTSU University', 'Sandip University', 'Joy University'],
      accent: '#3b82f6', accentRgb: '59,130,246', icon: iconCode, image: IMG.fullStack,
    },
    {
      title: 'Robotic Process Automation',
      tag: 'with AI & ML',
      desc: 'Advanced RPA architecture, cognitive automation, and enterprise-grade intelligent process orchestration with AI-driven decision systems.',
      universities: ['LTSU University'],
      accent: '#8b5cf6', accentRgb: '139,92,246', icon: iconCpu, image: IMG.rpa,
    },
    {
      title: 'Computer Science & Design System',
      desc: 'Graduate-level design systems theory with advanced computing, HCI research, and UX architecture for digital product leadership.',
      universities: ['Canara Engineering College'],
      accent: '#ec4899', accentRgb: '236,72,153', icon: iconLayers, image: IMG.csDesign,
    },
  ],

  'MCA Online': [
    {
      title: 'Full Stack Development & Testing',
      tag: 'with AI & ML',
      desc: 'Advanced full stack engineering with AI-powered testing, microservices architecture, and cloud-native deployment — fully online.',
      universities: ['Jain University'],
      accent: '#3b82f6', accentRgb: '59,130,246', icon: iconCode, image: IMG.fullStack,
    },
  ],

  'MBA': [
    {
      title: 'Fintech & Data Analytics',
      desc: 'Master financial technology, algorithmic systems, digital banking, and data-driven decision making to lead the future of global finance.',
      universities: ['LTSU University', 'Sandip University', 'Canara Engineering College', 'Sanjay Ghodawat University', 'Joy University'],
      accent: '#14b8a6', accentRgb: '20,184,166', icon: iconTrending, image: IMG.fintech,
    },
  ],

  'MBA Online': [
    {
      title: 'Fintech & Data Analytics',
      desc: 'Master financial technology, algorithmic systems, digital banking, and data-driven decision making — fully online, flexible schedule.',
      universities: ['Jain University'],
      accent: '#14b8a6', accentRgb: '20,184,166', icon: iconTrending, image: IMG.fintech,
    },
  ],
}

// ── Active state ──────────────────────────────────────────────────────────────
const activeDegree = ref<DegreeName>('B.Tech')
</script>

<style scoped>
/* ── Section ──────────────────────────────────────────────────────────────── */
.programs-section {
  padding: 6rem 2rem;
  position: relative;
  z-index: 10;
}

.programs-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* ── Header ───────────────────────────────────────────────────────────────── */
.section-header {
  text-align: center;
  margin-bottom: 3.5rem;
}

.section-title {
  font-size: clamp(2.2rem, 5.5vw, 4rem);
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 1.25rem;
  letter-spacing: -0.02em;
  line-height: 1.15;
}

.section-subtitle {
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.75;
}

.gradient-text {
  background: linear-gradient(135deg, #ff7800, #ffbb3f);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ── Degree filter panel ──────────────────────────────────────────────────── */
.filter-wrap {
  margin-bottom: 3.5rem;
  display: flex;
  width: 100%;
}

.filter-panel {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 2.5rem;
  width: 100%;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 1.8rem 2.2rem;
  flex-wrap: wrap;
  box-shadow: var(--shadow-elevated);
  transition: background 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
}

/* Group: label + chips row */
.deg-group {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.group-label {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--text-secondary);
}

.group-label svg {
  flex-shrink: 0;
}

.deg-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

/* Degree chip */
.deg-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.52rem 1.05rem;
  background: var(--feature-bg);
  border: 1px solid var(--border-color);
  border-radius: 50px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-secondary);
  cursor: pointer;
  transition:
    background 0.24s ease,
    border-color 0.24s ease,
    color 0.24s ease,
    box-shadow 0.24s ease;
}

.deg-chip:hover {
  background: var(--feature-hover-bg);
  color: var(--text-primary);
  border-color: var(--feature-hover-border);
  box-shadow: var(--shadow-elevated);
}

.deg-chip.active {
  background: rgba(255, 187, 63, 0.22);
  border-color: rgba(255, 187, 63, 0.65);
  color: #ffbb3f;
  box-shadow:
    0 0 22px rgba(255, 187, 63, 0.22),
    inset 0 1px 0 rgba(255, 255, 255, 0.08);
}

/* Count badge on chip */
.chip-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 5px;
  border-radius: 50px;
  background: var(--feature-hover-bg);
  font-size: 0.64rem;
  font-weight: 700;
  color: var(--text-muted);
  transition: background 0.24s ease, color 0.24s ease;
}

.deg-chip.active .chip-count {
  background: rgba(255, 187, 63, 0.28);
  color: #ffbb3f;
}

/* Vertical divider between UG and PG groups */
.filter-divider {
  width: 1px;
  align-self: stretch;
  background: var(--border-color);
  margin: 0.25rem 0;
  flex-shrink: 0;
  transition: background 0.3s ease;
}

/* ── Transition ───────────────────────────────────────────────────────────── */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: opacity 0.26s ease, transform 0.26s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ── Cards grid ───────────────────────────────────────────────────────────── */
.programs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
  align-items: start;
  max-width: 100%;
}

@supports (grid-template-columns: subgrid) {
  .programs-grid {
    justify-content: center;
  }
}

.programs-grid {
  justify-content: center;
}

/* ── Card ─────────────────────────────────────────────────────────────────── */
.prog-card {
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  overflow: hidden;
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  transition:
    transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.32s ease,
    box-shadow 0.32s ease,
    background 0.3s ease;
}

[data-theme="light"] .prog-card {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

.prog-card:hover {
  transform: translateY(-6px);
  border-color: rgba(var(--accent-rgb), 0.38);
  box-shadow:
    0 10px 46px rgba(var(--accent-rgb), 0.15),
    0 0 0 1px rgba(var(--accent-rgb), 0.1);
}

/* ── Accent bar ───────────────────────────────────────────────────────────── */
.prog-top-bar {
  height: 3px;
  background: linear-gradient(90deg, var(--accent), rgba(var(--accent-rgb), 0.25));
  flex-shrink: 0;
}

/* ── Media (image) ────────────────────────────────────────────────────────── */
.prog-media {
  position: relative;
  height: 148px;
  overflow: hidden;
  flex-shrink: 0;
  background: rgba(var(--accent-rgb), 0.05);
}

/* The actual image */
.media-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.55s cubic-bezier(0.4, 0, 0.2, 1), filter 0.4s ease;
}

.prog-card:hover .media-img {
  transform: scale(1.06);
  filter: brightness(0.92);
}

/* Bottom darkening overlay so the image blends into the card body */
.media-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg,
      rgba(6, 5, 24, 0) 0%,
      rgba(6, 5, 24, 0) 55%,
      rgba(6, 5, 24, 0.45) 100%);
  z-index: 1;
  pointer-events: none;
}

[data-theme="light"] .media-overlay {
  background: linear-gradient(180deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0) 55%,
      rgba(255, 255, 255, 0.45) 100%);
}

/* Small monochrome icon badge — top-left corner */
.media-icon-badge {
  position: absolute;
  top: 0.75rem;
  left: 0.75rem;
  z-index: 2;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: rgba(6, 5, 24, 0.62);
  border: 1px solid rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.9);
  transition: background 0.32s ease, border-color 0.32s ease, transform 0.32s ease;
}

[data-theme="light"] .media-icon-badge {
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
}

.prog-card:hover .media-icon-badge {
  background: rgba(6, 5, 24, 0.78);
  border-color: rgba(var(--accent-rgb), 0.4);
  transform: scale(1.07);
}

/* ── Card body ────────────────────────────────────────────────────────────── */
.prog-body {
  padding: 1.4rem 1.55rem 1.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  flex: 1;
}

/* Ghost number */
.prog-number {
  position: absolute;
  top: calc(148px + 3px + 0.95rem);
  right: 1.35rem;
  font-size: 2.4rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1;
  color: rgba(var(--accent-rgb), 0.09);
  pointer-events: none;
  user-select: none;
  transition: color 0.35s ease;
}

.prog-card:hover .prog-number {
  color: rgba(var(--accent-rgb), 0.18);
}

/* ── Title + tag ──────────────────────────────────────────────────────────── */
.prog-titles {
  display: flex;
  flex-direction: column;
  gap: 0.22rem;
}

.prog-title {
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.35;
  padding-right: 2.4rem;
}

.prog-tag {
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--accent);
  opacity: 0.8;
  margin: 0;
}

/* ── Description ──────────────────────────────────────────────────────────── */
.prog-desc {
  font-size: 0.82rem;
  color: var(--text-secondary);
  line-height: 1.7;
  margin: 0;
}

/* ── University partners ──────────────────────────────────────────────────── */
.prog-unis {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  border-top: 1px solid var(--border-color);
  padding-top: 0.85rem;
  margin-top: auto;
  transition: border-color 0.3s ease;
}

.unis-label {
  display: flex;
  align-items: center;
  gap: 0.38rem;
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted);
}

.uni-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.38rem;
}

.uni-chip {
  padding: 0.25rem 0.68rem;
  border-radius: 50px;
  background: rgba(var(--accent-rgb), 0.07);
  border: 1px solid rgba(var(--accent-rgb), 0.2);
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-secondary);
  letter-spacing: 0.01em;
  transition: background 0.24s ease, border-color 0.24s ease, color 0.24s ease;
}

.prog-card:hover .uni-chip {
  background: rgba(var(--accent-rgb), 0.13);
  border-color: rgba(var(--accent-rgb), 0.32);
  color: var(--text-primary);
}

/* ── CTA ──────────────────────────────────────────────────────────────────── */
.prog-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.76rem;
  font-weight: 700;
  color: var(--accent);
  background: rgba(var(--accent-rgb), 0.08);
  border: 1px solid rgba(var(--accent-rgb), 0.22);
  border-radius: 50px;
  padding: 0.48rem 1.05rem;
  cursor: pointer;
  letter-spacing: 0.02em;
  align-self: flex-start;
  transition:
    background 0.26s ease,
    border-color 0.26s ease,
    gap 0.2s ease;
}

.prog-cta:hover {
  background: rgba(var(--accent-rgb), 0.15);
  border-color: rgba(var(--accent-rgb), 0.42);
  gap: 0.58rem;
}

/* ── Responsive ───────────────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .filter-panel {
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.25rem 1.5rem;
  }

  .filter-divider {
    width: 100%;
    height: 1px;
    align-self: auto;
    margin: 0;
  }
}

@media (max-width: 680px) {
  .programs-section {
    padding: 5rem 1rem;
  }

  .filter-wrap {
    justify-content: stretch;
  }

  .filter-panel {
    width: 100%;
  }

  .programs-grid {
    grid-template-columns: 1fr;
    gap: 1.1rem;
  }

  .section-title {
    font-size: clamp(1.85rem, 7vw, 2.6rem);
  }
}
</style>
